// src/pages/worker/WorkersList/WorkersList.styles.ts
import { styled, alpha } from '@mui/material/styles';
import { Box, Typography, Button, TextField, Table, TableHead, TableBody, TableCell, Tabs, Tab } from '@mui/material';
import { primaryColor } from '../../../utils/theme';

// --- 1. Summary Cards Section ---

export const SummaryCardsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', // Reduced minmax
  gap: theme.spacing(2), // Reduced gap
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(1),
}));

export const SummaryCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2), // Reduced padding
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
  height: '100%',
}));

export const SummaryIconWrapper = styled(Box)<{ colorName: 'primary' | 'info' | 'error' }>(({ theme, colorName }) => ({
  backgroundColor: alpha(theme.palette[colorName]?.main || theme.palette.grey[300], 0.1),
  borderRadius: '8px',
  padding: theme.spacing(1), // Reduced padding
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    fontSize: '2rem', // Reduced from 2.5rem
    color: theme.palette[colorName]?.main || theme.palette.grey[600],
  },
}));

export const SummaryContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const SummaryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem', // Reduced from 1rem
  fontWeight: 500,
  color: theme.palette.grey[600],
  marginBottom: theme.spacing(0.25),
}));

export const SummaryValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem', // Reduced from 1.8rem
  fontWeight: 700,
  color: theme.palette.custom.sidebarText,
  lineHeight: 1,
  marginBottom: theme.spacing(0.25),
}));

export const SummaryDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.grey[500],
}));


// --- 2. List Header and Action Button ---

export const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  // Removed marginTop as SummaryCardsContainer handles the top spacing
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem', // Reduced from 1.8rem
  fontWeight: 600,
  color: theme.palette.custom.sidebarText,
}));

export const AddWorkerButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  borderRadius: '8px',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  color: theme.palette.primary.main,
  fontSize: '0.9rem', // Reduced from 1.1rem
  fontWeight: '600',
  textTransform: 'none',
  padding: '4px 16px', // Reduced from 6px 25px
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
  },

  '&:active': {
    boxShadow: 'none',
    backgroundColor: theme.palette.primary.main,
  },

  '& .MuiButton-startIcon': {
    marginRight: '6px',
    '& svg': { fontSize: '18px' }
  },
}));

// --- 3. Filter/Search Area ---

export const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  width: '100%',
  flexWrap: 'wrap',
}));

export const SearchInput = styled(TextField)(({ theme }) => ({
  flex: '1 1 240px',
  maxWidth: 240,
  '& .MuiOutlinedInput-root': {
    height: '36px', 
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.white.main,
    fontSize: '0.85rem',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&.Mui-focused fieldset': {
      borderColor: primaryColor,
      borderWidth: '1px',
    },
    '& .MuiInputAdornment-root': {
      color: theme.palette.grey[500],
      '& svg': { fontSize: '18px' }
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 12px',
  }
}));

export const DropdownSelect = styled(TextField)(({ theme }) => ({
  width: '180px',
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.white.main,
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&.Mui-focused fieldset': {
      borderColor: primaryColor,
      borderWidth: '1px',
    },
  },
}));

// --- 4. Status Tabs ---

export const StatusTabsContainer = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  marginBottom: theme.spacing(3),
}));

export const StyledTabs = styled(Tabs)({
  '& .MuiTabs-indicator': {
    backgroundColor: primaryColor,
  },
});

export const StatusTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.grey[600],
  padding: theme.spacing(1, 2),
  minHeight: '48px',
  '&.Mui-selected': {
    color: primaryColor,
  },
}));


// --- 5. Data Table ---

export const StyledTableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  overflowX: 'auto',
  boxShadow: 'none',
}));

export const StyledTable = styled(Table)({
  minWidth: 800,
  borderCollapse: 'separate',
  borderSpacing: 0,
});

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,

  '& th': {
    backgroundColor: 'inherit',
    color: theme.palette.custom.sidebarText,
    fontWeight: 600,
    fontSize: '0.85rem',
    borderBottom: 'none',
    padding: theme.spacing(1.5, 2),
  },
}));

export const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& tr:nth-of-type(odd)': {
    backgroundColor: theme.palette.white.main, // White rows
  },
  '& tr:nth-of-type(even)': {
    backgroundColor: theme.palette.primary.light, // Very light grey stripe
  },
  '& tr:last-child td': {
    borderBottom: 0,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: '0.85rem',
  color: theme.palette.custom.sidebarText,
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  padding: theme.spacing(1.5, 2),
  whiteSpace: 'nowrap',
}));


// --- 6. Status Tags and Pagination ---

interface StatusTagProps {
  status: 'Active' | 'Pending' | 'Blocked' | string;
}

export const StatusTag = styled(Box)<StatusTagProps>(({ theme, status }) => {
  let bgColor: string;
  let color: string;

  switch (status) {
    case 'active':
      bgColor = alpha(primaryColor, 0.15);
      color = primaryColor;
      break;
    case 'pending':
      bgColor = alpha(theme.palette.warning.light, 0.5);
      color = theme.palette.warning.dark;
      break;
    case 'blocked':
      bgColor = alpha(theme.palette.error.main, 0.15);
      color = theme.palette.error.main;
      break;
    case 'invitation_sent':
      bgColor = alpha(theme.palette.error.main, 0.15);
      color = theme.palette.link.main;
      break;
    default:
      bgColor = theme.palette.grey[100];
      color = theme.palette.grey[600];
  }

  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '4px',
    // textAlign: 'center',
    width: 'fit-content',
    textTransform: 'sentence case',
    // float: "none",
    // margin: "0 auto",
  };
});

export const PaginationFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  color: theme.palette.grey[600],
  fontSize: '0.85rem',
  paddingBottom: 0,
}));

export const PaginationControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),

  // Targets all root buttons: "Prev" and "Next"
  '& > .MuiButton-root': {
    textTransform: 'none',
    fontSize: '0.85rem',
    minWidth: '35px',
    padding: theme.spacing(0.5, 1.5),
    height: '32px',
    boxShadow: 'none',
  },

  // 1. Style for the 'Prev' button (first child button - Outlined look from image_b3999e.png)
  '& > .MuiButton-root:first-of-type': {
    backgroundColor: theme.palette.white.main,
    color: theme.palette.grey[600],
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    minWidth: '60px',

    '&:hover': {
      backgroundColor: theme.palette.grey[50],
      border: `1px solid ${theme.palette.grey[400]}`,
    },
    '&.Mui-disabled': {
      color: theme.palette.grey[400],
      borderColor: theme.palette.grey[200],
    }
  },

  // 3. Style for the 'Next' button (last child button - Plain Text/Link look)
  '& > .MuiButton-root:last-of-type': {
    backgroundColor: 'transparent',
    color: theme.palette.grey[600],
    border: 'none',
    minWidth: '35px', // Smaller width to look like text
    padding: theme.spacing(0.5, 1),

    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline', // Add underline on hover for link feel
    },
    '&.Mui-disabled': {
      color: theme.palette.grey[400],
    }
  },

  // 2. Style for the number buttons group (MuiButtonGroup, which is the middle element)
  '& .MuiButtonGroup-root': {
    boxShadow: 'none',
    border: 'none',
    gap: 0,
  },

  // Style for individual number buttons within the group
  '& .MuiButtonGroup-root .MuiButton-root': {
    textTransform: 'none',
    minWidth: '32px',
    height: '32px',
    padding: 0,
    fontSize: '0.85rem',
    fontWeight: 500,
    color: theme.palette.custom.sidebarText,
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.white.main,
    boxShadow: 'none',

    // Active button style
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: primaryColor,
      color: theme.palette.white.main,
      borderColor: primaryColor,
      zIndex: 1,
      boxShadow: 'none',
    },

    '&:hover': {
      backgroundColor: theme.palette.grey[50],
      borderColor: theme.palette.grey[400],
    },

    // Connect the buttons visually by removing inner borders
    '&:not(:last-of-type)': {
      borderRight: 'none',
    },

    // Ensure all buttons in the group have zero border radius initially
    borderRadius: 0,
  },

  // Apply rounded corners only to the outer edges of the group
  '& .MuiButtonGroup-root .MuiButton-root:first-of-type': {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,
  },
  '& .MuiButtonGroup-root .MuiButton-root:last-of-type': {
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
}));

export const WorkerListContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5), // Reduced from 4
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.palette.custom.borderRadius,
}));